/**
 * Mutation hooks for EventEntry operations
 * Replaces complete(), save(), delete(), deleteAll() methods on EventEntry model
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EventEntry, defaultEventEntry, toEventEntryPayload, getEventStartDateTime } from '../Model/Coach/Schedule/EventEntry';
import dayjs from '../Utils/dayjs';
import { useBackend } from './useBackend';

export function useCompleteEvent() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ event, linkedData = {} }: { event: EventEntry; linkedData?: Record<string, any> }) =>
      backend.post(`/coach/schedule/event/${event.id}/log`, {
        linkedData,
        date: new Date().toISOString(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventEntry'] });
      queryClient.invalidateQueries({ queryKey: ['activitySchedule'] });
    },
  });
}

export function useSaveEvent() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (event: EventEntry) =>
      backend.post('/coach/schedule/event', toEventEntryPayload(event)).then((result) => defaultEventEntry(result)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventEntry'] });
      queryClient.invalidateQueries({ queryKey: ['activitySchedule'] });
    },
  });
}

export function useDeleteEvent() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (event: EventEntry) => backend.delete(`/coach/schedule/event/${event.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventEntry'] });
      queryClient.invalidateQueries({ queryKey: ['activitySchedule'] });
    },
  });
}

export function useDeleteAllEvents() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (event: EventEntry) => {
      const id = event.parentId ?? event.id;
      if (event.groupId) {
        return backend.delete(`/coach/schedule/event/group/${event.groupId}`);
      }
      return backend.delete(`/coach/schedule/event/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventEntry'] });
      queryClient.invalidateQueries({ queryKey: ['activitySchedule'] });
    },
  });
}

export function useLogCompleted() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      objectType,
      objectId,
      date = new Date(),
      linkedData = {},
    }: {
      objectType: string;
      objectId: string;
      date?: Date;
      linkedData?: Record<string, any>;
    }) =>
      backend
        .post('/coach/schedule/event/log', {
          objectType,
          objectId,
          date: dayjs(date).format('YYYY-MM-DD'),
          linkedData,
        })
        .then((res) => (res ? defaultEventEntry(res) : undefined)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventEntry'] });
      queryClient.invalidateQueries({ queryKey: ['activitySchedule'] });
    },
  });
}

export function useDeleteSubscribedEvent() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => backend.delete(`/coach/schedule/event/subscription/${eventId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscribedEvent'] });
      queryClient.invalidateQueries({ queryKey: ['activitySchedule'] });
    },
  });
}

export function useEventParent(objectType?: string, objectId?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['eventEntry', 'parent', objectType, objectId],
    queryFn: async () => {
      const res = await backend.get('/coach/schedule/event', { objectType, objectId, parent: true, size: 1 });
      if ((res ?? []).length > 0) {
        const event = defaultEventEntry(res[0]);
        if (
          event.recurringPattern ||
          Number(getEventStartDateTime(event).format('YYYYMMDD')) >= Number(dayjs().format('YYYYMMDD'))
        ) {
          return event;
        }
      }
      return undefined;
    },
    enabled: !!objectType && !!objectId,
  });
}
